import { useEffect, useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import {
  Button,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getReceivedInvoices } from '../../services/ElectronicBillingService';
import LinkIcon from '@mui/icons-material/Link';
import ReceivedInvoiceSyncDialog from './ReceivedInvoiceSyncDialog';
import SupplierUnlinkedItemsDialog from './SupplierUnlinkedItemsDialog';
import MonthYearSelector from '../../components/Reports/Sales/MonthYearSelector';
import { formatDate } from '../../utils/DateUtils';
import { getIcon } from '../../utils/IconHandler';
import dayjs from 'dayjs';
import { formatMoney } from '../../utils/currency';

const mapCfeTypeToLabel = (type) => {
  switch (type) {
    case '101':
      return 'Ticket';
    case '111':
      return 'Factura';
    case '112':
      return 'NC-Factura';
    case '102':
      return 'NC-Ticket';
    default:
      return `Tipo ${type}`;
  }
};

const formatCurrency = (value) => {
  const number = Number(value);
  return isNaN(number)
    ? '-'
    : formatMoney(number, undefined, { symbol: false });
};

const formatQuantity = (value) => {
  const number = Number(value);
  return isNaN(number)
    ? null
    : new Intl.NumberFormat('es-UY', { maximumFractionDigits: 2 }).format(number);
};

const numberOrNull = (value) => {
  const number = Number(value);
  return isNaN(number) ? null : number;
};

const calculateLineAmount = (item) => {
  if (item?.amount != null) return Number(item.amount);
  const quantity = numberOrNull(item?.quantity);
  const unitPrice = numberOrNull(item?.unitPrice);
  if (quantity == null || unitPrice == null) return null;

  const subtotal = quantity * unitPrice;
  const discountAmount = numberOrNull(item?.discountAmount);
  const discountPercent = numberOrNull(item?.discountPercent);
  const discount = discountAmount != null
    ? discountAmount
    : (discountPercent != null ? subtotal * discountPercent / 100 : 0);

  return subtotal - discount;
};

const calculateUnitDiscount = (item) => {
  const quantity = numberOrNull(item?.quantity);
  const unitPrice = numberOrNull(item?.unitPrice);
  const discountAmount = numberOrNull(item?.discountAmount);
  const discountPercent = numberOrNull(item?.discountPercent);
  if (discountAmount != null && discountAmount !== 0 && quantity) return discountAmount / quantity;
  if (discountPercent != null && discountPercent !== 0 && unitPrice != null) return unitPrice * discountPercent / 100;

  const lineAmount = calculateLineAmount(item);
  if (quantity && unitPrice != null && lineAmount != null) {
    const unitDiscount = unitPrice - (lineAmount / quantity);
    return Math.abs(unitDiscount) > 0.01 ? unitDiscount : null;
  }
  return null;
};

const calculateFinalUnitPrice = (item) => {
  const quantity = numberOrNull(item?.quantity);
  const lineAmount = calculateLineAmount(item);
  if (!quantity || lineAmount == null) return null;
  return lineAmount / quantity;
};

const dedupeInvoiceItems = (items = []) => {
  const byLine = new Map();

  items.forEach((item) => {
    const key = [
      (item?.description || '').trim().toLowerCase(),
      numberOrNull(item?.quantity)?.toString() || '',
      numberOrNull(item?.unitPrice)?.toString() || '',
    ].join('|');

    if (!key.trim()) return;

    const current = byLine.get(key);
    if (!current || scoreInvoiceItem(item) > scoreInvoiceItem(current)) {
      byLine.set(key, item);
    }
  });

  return Array.from(byLine.values());
};

const scoreInvoiceItem = (item) => {
  const unitPrice = numberOrNull(item?.unitPrice);
  const finalUnitPrice = calculateFinalUnitPrice(item);
  const unitDiscount = calculateUnitDiscount(item);
  return [
    unitDiscount != null && unitDiscount !== 0 ? 4 : 0,
    finalUnitPrice != null && unitPrice != null && Math.abs(finalUnitPrice - unitPrice) > 0.01 ? 3 : 0,
    calculateLineAmount(item) != null ? 2 : 0,
    unitPrice != null ? 1 : 0,
  ].reduce((sum, value) => sum + value, 0);
};

const ReceivedInvoicesTable = ({ selectedRut, selectedLocationId = null, embedded = false }) => {
  const { t } = useTranslation();

  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [month, setMonth] = useState(dayjs().month() + 1);
  const [year, setYear] = useState(dayjs().year());
  const [totalCount, setTotalCount] = useState(0);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [syncInvoice, setSyncInvoice] = useState(null);
  const [supplierItemsOpen, setSupplierItemsOpen] = useState(false);

  // Debounce para el término de búsqueda
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      // Resetear a la primera página cuando cambie la búsqueda
      setPaginationModel(prev => ({ ...prev, page: 0 }));
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (selectedRut === null) {
      return;
    }
    
    const fetch = async () => {
      setLoading(true);
      try {
        const data = await getReceivedInvoices({ 
          month, 
          year, 
          page: paginationModel.page, 
          pageSize: paginationModel.pageSize,
          search: debouncedSearchTerm,
          rut: selectedRut
        });
        
        // Asegurar que data tenga la estructura esperada
        const invoices = data?.invoices || [];
        const totalCount = data?.totalCount || 0;
        
        setInvoices(invoices);
        setTotalCount(totalCount);
      } catch (e) {
        console.error('Error fetching received invoices', e);
        setInvoices([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    };

    fetch();
  }, [month, year, paginationModel.page, paginationModel.pageSize, debouncedSearchTerm, selectedRut]);

  // El filtrado ahora se hace en el servidor, no necesitamos filteredInvoices


  const columns = [
    { field: 'serie', headerName: 'Serie', flex: 0.5 },
    { field: 'nro', headerName: 'Nro', flex: 0.8, align: 'center', headerAlign: 'center' },
    {
      field: 'tipoCfe',
      headerName: 'Tipo CFE',
      flex: 0.8,
      align: 'center',
      headerAlign: 'center',
      valueFormatter: (params) => mapCfeTypeToLabel(params),
    },
    {
      field: 'fecha',
      headerName: 'Fecha',
      flex: 1,
      align: 'center',
      headerAlign: 'center',
      valueFormatter: (params) => formatDate(params),
    },
    {
      field: 'provider',
      headerName: 'Proveedor',
      flex: 1.5,
      minWidth: 220,
      sortable: false,
      valueGetter: (_value, row) => row?.emisorFantasyName || row?.emisorName || row?.rucEmisor || '',
      renderCell: (params) => {
        const fantasyName = params.row.emisorFantasyName?.trim();
        const legalName = params.row.emisorName?.trim();
        const primary = fantasyName || legalName || 'Sin nombre';
        const secondary = [
          fantasyName && legalName && fantasyName !== legalName ? legalName : null,
          params.row.rucEmisor ? `RUC ${params.row.rucEmisor}` : null,
        ].filter(Boolean).join(' · ');

        return (
          <Box
            sx={{
              minWidth: 0,
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap title={primary}>
              {primary}
            </Typography>
            {secondary && (
              <Typography variant="caption" color="text.secondary" noWrap title={secondary}>
                {secondary}
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      field: 'moneda',
      headerName: 'Moneda',
      flex: 0.6,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'iva',
      headerName: 'IVA',
      flex: 1,
      align: 'center',
      headerAlign: 'center',
      valueFormatter: (params) => {
        return formatCurrency(params);
      }
    },
    {
      field: 'montoTotal',
      headerName: 'Monto Total',
      flex: 1,
      align: 'center',
      headerAlign: 'center',
      valueFormatter: (params) => {
        return formatCurrency(params);
      }
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      flex: 0.7,
      minWidth: 110,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box>
          <IconButton onClick={() => setSelectedInvoice(params.row)} title="Ver detalle" size="small">
            {getIcon('eye')}
          </IconButton>
          <IconButton onClick={() => setSyncInvoice(params.row)} title="Vincular ítems a insumos" size="small">
            <LinkIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  const Root = embedded ? Box : Paper;
  const rootProps = embedded
    ? { sx: { mb: 2 } }
    : { sx: { padding: 2, mb: 2 } };

  return (
    <Root {...rootProps}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
        <TextField
          label={t('search')}
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ width: { xs: '100%', sm: 300 } }}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Button variant="outlined" size="small" onClick={() => setSupplierItemsOpen(true)} disabled={!selectedLocationId}>
            Vincular ítems por proveedor
          </Button>
          <Box sx={{ width: { xs: '100%', sm: 220 } }}>
            <MonthYearSelector month={month} year={year} setMonth={setMonth} setYear={setYear} />
          </Box>
        </Box>
      </Box>

      <DataGrid
        getRowId={(row) => row.id ?? `${row.serie}-${row.nro}-${row.rucEmisor ?? ''}`}
        loading={loading}
        rows={invoices}
        columns={columns}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        pageSizeOptions={[10, 25, 50]}
        paginationMode="server"
        rowCount={totalCount}
        rowHeight={64}
        autoHeight
      />

      <ReceivedInvoiceDetailDialog
        invoice={selectedInvoice}
        onClose={() => setSelectedInvoice(null)}
      />
      <SupplierUnlinkedItemsDialog
        open={supplierItemsOpen}
        onClose={() => setSupplierItemsOpen(false)}
        onLink={(syntheticInvoice) => {
          setSupplierItemsOpen(false);
          setSyncInvoice(syntheticInvoice);
        }}
      />
      <ReceivedInvoiceSyncDialog
        invoice={syncInvoice}
        locationId={selectedLocationId}
        onClose={() => setSyncInvoice(null)}
      />
    </Root>
  );
};

const ReceivedInvoiceDetailDialog = ({ invoice, onClose }) => {
  const items = dedupeInvoiceItems(invoice?.items || []);
  const providerName = invoice?.emisorFantasyName || invoice?.emisorName || 'Sin nombre';
  const legalName = invoice?.emisorName && invoice.emisorName !== providerName ? invoice.emisorName : null;

  return (
    <Dialog open={Boolean(invoice)} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Detalle de factura recibida</DialogTitle>
      <DialogContent dividers>
        {invoice && (
          <Box sx={{ display: 'grid', gap: 2 }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1.4fr 1fr 1fr' },
                gap: 2,
                p: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                bgcolor: 'grey.50',
              }}
            >
              <Box>
                <Typography variant="caption" color="text.secondary">Proveedor</Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{providerName}</Typography>
                {legalName && <Typography variant="body2" color="text.secondary">{legalName}</Typography>}
                {invoice.rucEmisor && <Typography variant="body2" color="text.secondary">RUC {invoice.rucEmisor}</Typography>}
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Comprobante</Typography>
                <Typography variant="body2">{mapCfeTypeToLabel(invoice.tipoCfe)} {invoice.serie}-{invoice.nro}</Typography>
                <Typography variant="body2" color="text.secondary">{formatDate(invoice.fecha)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Totales</Typography>
                <Typography variant="body2">IVA: {formatCurrency(invoice.iva)} {invoice.moneda}</Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Total: {formatCurrency(invoice.montoTotal)} {invoice.moneda}</Typography>
              </Box>
            </Box>

            {items.length ? (
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 440 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Producto / servicio</TableCell>
                      <TableCell align="right">Cantidad</TableCell>
                      <TableCell align="right">Precio unitario</TableCell>
                      <TableCell align="right">Descuento unit.</TableCell>
                      <TableCell align="right">Precio final unit.</TableCell>
                      <TableCell align="right">Importe</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.map((item, index) => (
                      <TableRow key={`${item.description}-${index}`}>
                        <TableCell sx={{ fontWeight: 500 }}>{item.description || '-'}</TableCell>
                        <TableCell align="right">{formatQuantity(item.quantity) || '-'}</TableCell>
                        <TableCell align="right">{item.unitPrice != null ? formatCurrency(item.unitPrice) : '-'}</TableCell>
                        <TableCell align="right">{calculateUnitDiscount(item) != null ? formatCurrency(calculateUnitDiscount(item)) : '-'}</TableCell>
                        <TableCell align="right">{calculateFinalUnitPrice(item) != null ? formatCurrency(calculateFinalUnitPrice(item)) : '-'}</TableCell>
                        <TableCell align="right">{calculateLineAmount(item) != null ? formatCurrency(calculateLineAmount(item)) : '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography color="text.secondary">
                FacturaLista no envió detalle de ítems para este comprobante.
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReceivedInvoicesTable;
